import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { JobState, JobStatus, RingCentralAutoAttendantReportEntry } from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { RingCentralAutoAttendantsPollingContext } from './ring-central-auto-attendants-polling.context';

@Injectable()
export class RingCentralAutoAttendantsContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _pollingContext = inject(RingCentralAutoAttendantsPollingContext);

  _stateSource = new ReplaySubject<RingCentralAutoAttendantReportEntry[]>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params['tenantId']}/exports/ring-central/${this._route.snapshot.params['id']}/auto-attendants`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._pollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null).pipe(switchMap(() => this._init()));
  }

  private _init(): Observable<void> {
    this._pollingContext.startPolling();

    return this._pollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._http.post<void>(`${this._baseUrl}/refresh`, null);
        }
        this._pollingContext.stopPolling();
        if (!status.error) {
          return this._http.get<RingCentralAutoAttendantReportEntry[]>(this._baseUrl);
        }
        return of(null);
      }),
      filter((entries) => !!entries),
      map((entries: RingCentralAutoAttendantReportEntry[]) => {
        this._stateSource.next(entries);
      }),
      take(1),
    );
  }
}
